import type { PropertyOption, TaxCategoryOption } from '@pet/shared';

export interface ReceiptLineItem {
  description: string;
  amountCents: number;
}

export interface IngestionStatusResponse {
  id: string;
  status: 'PENDING_AI' | 'AI_COMPLETE' | 'AI_FAILED' | 'CONFIRMED';
  mime: string;
  createdAt: string;
  vendor?: string;
  date?: string;
  totalCents?: number;
  lineItems?: ReceiptLineItem[];
  taxCategoryKey?: string;
  isCapital?: boolean;
  deMinimisEligible?: boolean;
  confidence?: number;
  // Build C: present (possibly null) on AI_COMPLETE; null on an extraction from before Build C.
  subtotalCents?: number | null;
  taxCents?: number | null;
}

export class ReceiptApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'ReceiptApiError';
  }
}

async function extractErrorMessage(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string };
    return body.error ?? `HTTP ${res.status}`;
  } catch {
    return `HTTP ${res.status}`;
  }
}

export async function uploadReceipt(file: File | Blob): Promise<IngestionStatusResponse> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch('/api/receipts/upload', {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  if (!res.ok) {
    throw new ReceiptApiError(res.status, await extractErrorMessage(res));
  }
  return res.json();
}

export async function getIngestion(id: string): Promise<IngestionStatusResponse> {
  const res = await fetch(`/api/receipts/${encodeURIComponent(id)}`, { credentials: 'include' });
  if (!res.ok) {
    throw new ReceiptApiError(res.status, await extractErrorMessage(res));
  }
  return res.json();
}

export async function getCategories(): Promise<TaxCategoryOption[]> {
  const res = await fetch('/api/receipts/categories', { credentials: 'include' });
  if (!res.ok) {
    throw new ReceiptApiError(res.status, await extractErrorMessage(res));
  }
  const data = (await res.json()) as { categories: TaxCategoryOption[] };
  return data.categories;
}

export async function getProperties(): Promise<PropertyOption[]> {
  const res = await fetch('/api/properties', { credentials: 'include' });
  if (!res.ok) {
    throw new ReceiptApiError(res.status, await extractErrorMessage(res));
  }
  const data = (await res.json()) as { properties: PropertyOption[] };
  return data.properties;
}
